import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { PublicFooter } from "../components/layout/PublicFooter";

export const CGVPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Biz360Logo />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={16} /> Retour
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10" data-testid="cgv-page">
        <h1 className="text-3xl font-bold mb-2" style={{ color: "#210875" }}>
          Conditions Générales de Vente (CGV)
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Dernière mise à jour : 30 mai 2026 — Version 1.0
        </p>

        <div
          className="p-4 rounded-lg mb-8 text-sm"
          style={{ backgroundColor: "#FDFCFF40", color: "#210875" }}
        >
          <strong>📜 À quoi servent ces CGV ?</strong> Les présentes Conditions Générales de Vente
          régissent les modalités de vente et de fourniture du service Biz360 (abonnement SaaS) à
          ses utilisateurs. Elles complètent — sans s'y substituer — les{" "}
          <a href="/mentions-legales-cgu" className="underline" style={{ color: "#210875" }}>
            Conditions Générales d'Utilisation
          </a>
          , qui régissent l'usage technique de la plateforme.
        </div>

        <Section title="Article 1 — Identification du vendeur">
          <p>
            Le service Biz360 est édité et commercialisé par :
          </p>
          <ul>
            <li>
              <strong>Tiphaine SAMBLAT</strong> — Entrepreneur individuel (auto-entrepreneur)
            </li>
            <li>
              <strong>SIREN :</strong> 913 345 682 — <strong>SIRET (siège) :</strong> 913 345 682 00037
            </li>
            <li>
              <strong>Code APE :</strong> 82.99Z — Autres activités de soutien aux entreprises n.c.a.
            </li>
            <li>
              <strong>Adresse :</strong> 48 C Boulevard de la Méditerranée, 31270 FROUZINS —
              France
            </li>
            <li>
              <strong>Email :</strong>{" "}
              <a href="mailto:t.samblat@gmail.com" className="underline" style={{ color: "#210875" }}>
                t.samblat@gmail.com
              </a>
            </li>
            <li>
              <strong>TVA intracommunautaire :</strong> Non assujettie — régime de la franchise en
              base de TVA (article 293 B du CGI)
            </li>
          </ul>
          <p className="text-xs italic mt-2">
            Ci-après désignée « <strong>l'Éditeur</strong> » ou « <strong>le Vendeur</strong> ».
          </p>
        </Section>

        <Section title="Article 2 — Objet et champ d'application">
          <p>
            Les présentes CGV ont pour objet de définir les droits et obligations des parties
            dans le cadre de la vente, à distance et en ligne, du service d'abonnement à
            l'application <strong>Biz360</strong>, accessible à l'adresse{" "}
            <a
              href="https://biz360.obusiness.fr"
              className="underline"
              style={{ color: "#210875" }}
            >
              biz360.obusiness.fr
            </a>
            .
          </p>
          <p>
            Biz360 est un service de pilotage de gestion à destination des auto-entrepreneurs et
            micro-entrepreneurs français : tableau de bord d'activité, calcul indicatif des
            cotisations Urssaf, alertes seuils TVA, suivi ventes/dépenses, déclaration Urssaf
            précalculée, et synchronisation avec des services bancaires partenaires (sous réserve
            d'activation).
          </p>
          <p>
            L'acceptation des présentes CGV est expresse, matérialisée par la coche d'acceptation
            au moment de la souscription. Toute commande implique l'acceptation pleine et entière
            des CGV en vigueur à la date de la commande.
          </p>
        </Section>

        <Section title="Article 3 — Caractéristiques du service">
          <p>
            <strong>3.1 Nature du service :</strong> Biz360 est un service SaaS (Software as a
            Service) accessible en ligne via un navigateur web standard, sans installation
            requise. La fourniture du service est numérique et immédiate dès la confirmation du
            paiement.
          </p>
          <p>
            <strong>3.2 Caractère indicatif des calculs :</strong> Les calculs présentés
            (cotisations Urssaf estimées, seuils TVA, CFE, etc.) sont fournis à titre{" "}
            <strong>strictement indicatif</strong>. L'Éditeur ne garantit ni l'exactitude, ni
            l'exhaustivité, ni l'adéquation des résultats à la situation particulière de
            l'utilisateur. Les informations affichées ne se substituent en aucun cas aux
            déclarations, calculs ou décisions des administrations et organismes compétents.
            L'utilisateur reste seul responsable de l'exactitude de ses déclarations
            officielles.
          </p>
          <p>
            <strong>3.3 Évolution du service :</strong> L'Éditeur se réserve le droit de faire
            évoluer le service (ajout, modification ou suppression de fonctionnalités). Toute
            modification substantielle sera notifiée à l'utilisateur par email au moins 30 jours
            avant son entrée en vigueur.
          </p>
        </Section>

        <Section title="Article 4 — Prix et modalités de paiement">
          <p>
            <strong>4.1 Prix :</strong> Les prix sont indiqués en euros (€) nets. L'Éditeur
            étant en franchise en base de TVA, <strong>la TVA n'est pas applicable — article
            293 B du CGI</strong>. Aucune TVA n'est facturée ni récupérable sur les
            abonnements.
          </p>
          <p>
            <strong>4.2 Offres disponibles à la date des présentes CGV :</strong>
          </p>
          <ul>
            <li>Abonnement mensuel : <strong>12 €/mois</strong></li>
            <li>Abonnement annuel : <strong>97 €/an</strong> (équivalent ~ 8,08 €/mois)</li>
          </ul>
          <p>
            L'Éditeur peut proposer ponctuellement des offres promotionnelles (essai gratuit
            étendu, tarif de lancement, code partenaire) dont les conditions précises seront
            affichées au moment de la souscription.
          </p>
          <p>
            <strong>4.3 Moyens de paiement :</strong> Le paiement s'effectue exclusivement en
            ligne par carte bancaire (Visa, Mastercard, CB, American Express) via la plateforme
            sécurisée <strong>Stripe</strong> (PCI-DSS niveau 1). L'Éditeur ne stocke aucune
            donnée bancaire.
          </p>
          <p>
            <strong>4.4 Récurrence :</strong> Les abonnements mensuels et annuels sont
            renouvelés <strong>tacitement</strong> à chaque échéance, sauf résiliation par
            l'utilisateur (voir Article 7).
          </p>
          <p>
            <strong>4.5 Défaut de paiement :</strong> En cas d'échec de paiement, l'Éditeur
            tentera 3 prélèvements automatiques sur 7 jours. À défaut de régularisation, l'accès
            au service sera suspendu jusqu'à résolution. La suspension de l'accès au service ne
            fait pas obstacle au droit de l'Éditeur de réclamer les sommes restant dues.
          </p>
          <p>
            <strong>4.6 Facturation :</strong> Une facture est émise automatiquement à chaque
            paiement et accessible depuis l'espace personnel de l'utilisateur.
          </p>
        </Section>

        <Section title="Article 5 — Souscription et formation du contrat">
          <p>
            <strong>5.1 Processus de commande :</strong> La souscription suit le parcours
            « Pay-First » suivant :
          </p>
          <ol className="list-decimal pl-5 space-y-1 text-sm">
            <li>Choix de l'offre depuis la page de souscription</li>
            <li>Paiement sécurisé via Stripe</li>
            <li>Création du compte utilisateur (email + mot de passe)</li>
            <li>Onboarding (saisie du profil auto-entrepreneur)</li>
            <li>Accès immédiat au service</li>
          </ol>
          <p>
            <strong>5.2 Confirmation :</strong> Un email de confirmation contenant la facture est
            envoyé à l'adresse email renseignée par l'utilisateur dans les minutes qui suivent
            la souscription.
          </p>
          <p>
            <strong>5.3 Formation du contrat :</strong> Le contrat de vente est réputé conclu à
            la date de validation du paiement par Stripe et matérialisé par l'envoi de l'email
            de confirmation.
          </p>
        </Section>

        <Section title="Article 6 — Absence de droit de rétractation">
          <p>
            Le service Biz360 est <strong>exclusivement destiné à des professionnels</strong>{" "}
            agissant dans le cadre de leur activité professionnelle (auto-entrepreneurs,
            micro-entrepreneurs, entrepreneurs individuels ou toute autre personne exerçant une
            activité économique indépendante).
          </p>
          <p>
            Lors de la souscription, l'utilisateur déclare expressément agir en qualité de
            professionnel dans le cadre de son activité. À défaut, l'Éditeur se réserve le
            droit de résilier l'abonnement sans préavis.
          </p>
          <p>
            En conséquence, les dispositions du Code de la consommation relatives au droit de
            rétractation des consommateurs <strong>ne sont pas applicables</strong> aux
            abonnements souscrits auprès de Biz360.
          </p>
          <p>
            Toute souscription est <strong>ferme et définitive</strong> à compter de la
            validation du paiement.
          </p>
          <p>
            Toutefois, à titre purement commercial et sans que cela constitue une obligation
            légale ou un précédent, l'Éditeur accorde une <strong>période de réflexion
            volontaire de 14 jours</strong> à compter de la souscription, pendant laquelle
            l'utilisateur peut demander le remboursement intégral de son abonnement par écrit
            à{" "}
            <a href="mailto:t.samblat@gmail.com" className="underline" style={{ color: "#210875" }}>
              t.samblat@gmail.com
            </a>
            , sous réserve d'un usage non intensif du service et appréciation par l'Éditeur au
            cas par cas. L'Éditeur peut modifier ou supprimer ce geste commercial pour les
            souscriptions futures à tout moment.
          </p>
        </Section>

        <Section title="Article 7 — Résiliation et fin de l'abonnement">
          <p>
            <strong>7.1 Résiliation par l'utilisateur :</strong> L'utilisateur peut résilier son
            abonnement à tout moment depuis son espace personnel (rubrique{" "}
            <em>Mes Paramètres → Abonnement</em>), sans frais ni pénalité. La résiliation prend
            effet à la fin de la période d'abonnement en cours déjà payée. L'accès au service est
            maintenu jusqu'à cette date.
          </p>
          <p>
            <strong>7.2 Absence de remboursement prorata temporis :</strong> Conformément à
            l'article L215-1 du Code de la consommation, aucun remboursement au prorata temporis
            n'est effectué pour la période restante après résiliation, sauf disposition légale
            contraire (rétractation, force majeure, défaut grave imputable à l'Éditeur).
          </p>
          <p>
            <strong>7.3 Résiliation par l'Éditeur :</strong> L'Éditeur peut résilier le contrat
            de plein droit, sans préavis ni indemnité, en cas de :
          </p>
          <ul>
            <li>Non-paiement persistant après mise en demeure restée infructueuse 7 jours</li>
            <li>Violation grave des CGU ou des présentes CGV</li>
            <li>Usage frauduleux, abusif ou contraire à la loi de l'application</li>
          </ul>
          <p>
            <strong>7.4 Conservation des données après résiliation :</strong> Conformément au
            RGPD, les données de l'utilisateur sont conservées pendant 30 jours après la
            résiliation effective <strong>afin de lui permettre de les récupérer ou de
            réactiver son compte</strong>. Certaines données pourront toutefois être conservées
            au-delà lorsque leur conservation est imposée par une obligation légale, fiscale,
            comptable ou réglementaire (voir{" "}
            <a href="/politique-confidentialite" className="underline" style={{ color: "#210875" }}>
              Politique de confidentialité
            </a>
            ). L'utilisateur peut exporter ses données à tout moment via son espace personnel.
          </p>
        </Section>

        <Section title="Article 8 — Garanties légales">
          <p>
            Conformément aux articles L217-3 et suivants du Code de la consommation et 1641 et
            suivants du Code civil, l'utilisateur bénéficie des garanties légales de conformité
            et de vices cachés.
          </p>
          <p>
            En cas de défaut grave imputable à l'Éditeur rendant le service inutilisable pendant
            une durée supérieure à 5 jours ouvrés consécutifs, l'utilisateur pourra demander :
          </p>
          <ul>
            <li>Soit la prolongation gratuite de son abonnement d'une durée équivalente</li>
            <li>Soit un remboursement au prorata de la période impactée</li>
          </ul>
        </Section>

        <Section title="Article 9 — Service après-vente et réclamations">
          <p>
            Pour toute question, réclamation ou demande relative à votre abonnement, vous pouvez
            contacter le service client :
          </p>
          <ul>
            <li>
              Par email :{" "}
              <a href="mailto:t.samblat@gmail.com" className="underline" style={{ color: "#210875" }}>
                t.samblat@gmail.com
              </a>
            </li>
            <li>
              Via le formulaire de contact accessible depuis l'application (rubrique{" "}
              <em>Contact</em>)
            </li>
          </ul>
          <p>
            <strong>Délai de réponse cible :</strong> 48 heures ouvrées.
          </p>
        </Section>

        <Section title="Article 10 — Règlement amiable des litiges">
          <p>
            Le service Biz360 est <strong>exclusivement réservé aux professionnels</strong>{" "}
            agissant dans le cadre de leur activité professionnelle. Lors de la souscription,
            l'utilisateur déclare expressément agir en qualité de professionnel ou exercer une
            activité économique indépendante. À ce titre, les relations contractuelles régies
            par les présentes CGV relèvent du droit commercial et <strong>non du droit de la
            consommation</strong> — la médiation de la consommation (article L612-1 du Code de
            la consommation) n'est donc pas applicable.
          </p>
          <p>
            En cas de différend, les parties s'engagent à rechercher une solution amiable
            préalablement à toute action judiciaire. Toute réclamation doit être adressée par
            écrit à{" "}
            <a href="mailto:t.samblat@gmail.com" className="underline" style={{ color: "#210875" }}>
              t.samblat@gmail.com
            </a>
            . L'Éditeur s'engage à répondre dans un délai de 30 jours.
          </p>
          <p>
            À défaut d'accord amiable dans un délai de 60 jours suivant la première
            réclamation, les juridictions compétentes pourront être saisies dans les conditions
            prévues à l'Article 13.
          </p>
        </Section>

        <Section title="Article 11 — Données personnelles">
          <p>
            Le traitement des données personnelles dans le cadre de la souscription et de
            l'exécution du service est encadré par la{" "}
            <a href="/politique-confidentialite" className="underline" style={{ color: "#210875" }}>
              Politique de confidentialité
            </a>
            , conforme au Règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés.
          </p>
        </Section>

        <Section title="Article 12 — Force majeure">
          <p>
            Aucune des parties ne pourra être tenue responsable d'un manquement à ses obligations
            résultant d'un cas de force majeure tel que défini par l'article 1218 du Code civil
            et la jurisprudence française (catastrophe naturelle, panne réseau majeure
            non-imputable à l'Éditeur, défaillance d'un sous-traitant essentiel, conflit armé,
            etc.).
          </p>
        </Section>

        <Section title="Article 13 — Droit applicable et juridiction compétente">
          <p>
            Les présentes CGV sont soumises au <strong>droit français</strong>.
          </p>
          <p>
            En cas de litige relatif à l'interprétation ou à l'exécution des présentes CGV, et
            à défaut de résolution amiable conformément à l'Article 10, le service Biz360 étant
            exclusivement destiné aux professionnels dans le cadre de leur activité, les
            relations contractuelles relèvent du <strong>droit commercial</strong>. À défaut
            d'accord amiable, <strong>compétence expresse est attribuée aux tribunaux du
            ressort de Toulouse</strong>, sous réserve des dispositions légales impératives
            applicables.
          </p>
        </Section>

        <Section title="Article 14 — Modification des CGV">
          <p>
            L'Éditeur se réserve le droit de modifier les présentes CGV à tout moment. Les
            modifications seront notifiées aux utilisateurs par email ou par notification dans
            l'application au moins <strong>30 jours</strong> avant leur entrée en vigueur. Les
            nouvelles CGV s'appliqueront aux abonnements en cours à la prochaine échéance de
            renouvellement.
          </p>
          <p>
            L'utilisateur n'acceptant pas les nouvelles CGV pourra résilier son abonnement sans
            frais avant leur entrée en vigueur.
          </p>
        </Section>
      </main>

      <PublicFooter />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h3 className="text-lg font-bold mb-3" style={{ color: "#210875" }}>
      {title}
    </h3>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
      {children}
    </div>
  </section>
);

export default CGVPage;
